import { createAsyncThunk } from "@reduxjs/toolkit";

import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { 
    GetAllResumes, 
    GetResumeByID, 
    CreateResume, 
    UpdateResume, 
    UpdateResumeStatus, 
    DeleteResume, 
    DownloadResume,
    RateResume,
    GetResumeRating,
    AddResumeComment,
    GetResumeComments,
    AddResumeTag,
    GetResumeTags,
} from "helpers/api_method";

export const getAllResume = createAsyncThunk("resumes/getAllResume", async (params: { page?: number; per_page?: number } | undefined = undefined, { rejectWithValue }) => {
    try {
        const response = await GetAllResumes(params);
        return response;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch resumes";
        return rejectWithValue(errorMessage);
    }
});

export const createResume = createAsyncThunk("resumes/createResume", async (data: any, { rejectWithValue }) => {
    try {
        const response: any = await CreateResume(data);

        // Backend convention: our API helper's interceptor returns the JSON body
        // which typically has shape: { status: boolean, message: string, data: any }.
        const apiStatus = response?.status;
        const apiMessage = response?.message;

        // If the API explicitly reports failure, treat it as an error
        if (apiStatus === false) {
            const errorMessage = apiMessage || "Failed to submit resume";
            toast.error(errorMessage, { autoClose: 5000 });
            return rejectWithValue(errorMessage);
        }

        toast.success("Resume submitted successfully", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || error || "Failed to submit resume";
        toast.error(errorMessage, { autoClose: 5000 });
        return rejectWithValue(errorMessage);
    }
});

export const getResumeByID = createAsyncThunk("resumes/getResumeByID", async (id: any, { rejectWithValue }) => {
    try {
        const response = await GetResumeByID(id);
        return response;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch resume";
        return rejectWithValue(errorMessage);
    }
});

export const updateResume = createAsyncThunk("resumes/updateResume", async (data: any, { rejectWithValue }) => {
    try {
        const { id, ...updateData } = data;
        const response = await UpdateResume(id, updateData);
        toast.success("Resume updated successfully", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to update resume";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});

export const updateResumeStatus = createAsyncThunk("resumes/updateResumeStatus", async (data: any, { rejectWithValue }) => {
    try {
        const response = await UpdateResumeStatus(data.id, data);
        toast.success("Resume status updated successfully", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to update resume status";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});

export const deleteResume = createAsyncThunk("resumes/deleteResume", async (id: any, { rejectWithValue }) => {
    try {
        const response = await DeleteResume(id);
        toast.success("Resume deleted successfully", { autoClose: 2000 });
        return response;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete resume";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});

export const downloadResume = createAsyncThunk("resumes/downloadResume", async (id: any, { rejectWithValue }) => {
    try {
        const response = await DownloadResume(id);
        return response;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to download resume";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});

// Rate Resume
export const rateResume = createAsyncThunk("resumes/rateResume", async ({ resumeId, data }: { resumeId: string; data: any }, { rejectWithValue }) => {
    try {
        const response = await RateResume(resumeId, data);
        toast.success("Resume rated successfully", { autoClose: 2000 });
        return { ...response, resumeId };
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to rate resume";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});

// Get Resume Rating
export const getResumeRating = createAsyncThunk("resumes/getResumeRating", async (resumeId: string, { rejectWithValue }) => {
    try {
        const response = await GetResumeRating(resumeId);
        return { ...response, resumeId };
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch resume rating";
        return rejectWithValue(errorMessage);
    }
});

// Add Resume Comment
export const addResumeComment = createAsyncThunk("resumes/addResumeComment", async ({ resumeId, data }: { resumeId: string; data: any }, { rejectWithValue }) => {
    try {
        const response = await AddResumeComment(resumeId, data);
        toast.success("Comment added successfully", { autoClose: 2000 });
        return { ...response, resumeId };
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to add comment";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});

// Get Resume Comments
export const getResumeComments = createAsyncThunk("resumes/getResumeComments", async (resumeId: string, { rejectWithValue }) => {
    try {
        const response = await GetResumeComments(resumeId);
        return { ...response, resumeId };
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch comments";
        return rejectWithValue(errorMessage);
    }
});

// Add Resume Tag
export const addResumeTag = createAsyncThunk("resumes/addResumeTag", async ({ resumeId, data }: { resumeId: string; data: any }, { rejectWithValue }) => {
    try {
        const response = await AddResumeTag(resumeId, data);
        toast.success("Tag added successfully", { autoClose: 2000 });
        return { ...response, resumeId };
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to add tag";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});

// Get Resume Tags
export const getResumeTags = createAsyncThunk("resumes/getResumeTags", async (resumeId: string, { rejectWithValue }) => {
    try {
        const response = await GetResumeTags(resumeId);
        return { ...response, resumeId };
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch tags";
        return rejectWithValue(errorMessage);
    }
});
